import { useTranslation } from 'react-i18next'
import { Button } from '#app/components/ui/button.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * Pricing tiers, left→right. Prices are placeholder numbers (data); every label
 * — tier name, cadence, blurb, feature bullets, CTA — is localized copy from the
 * `public` dictionary. `featured` marks the emphasized middle tier (ringed in
 * brand, carries the "Most popular" badge).
 */
const TIERS = [
	{ key: 'sprint', price: 'CHF 6k', featured: false },
	{ key: 'project', price: 'CHF 20k', featured: true },
	{ key: 'embedded', price: 'CHF 12k', featured: false },
] as const

/**
 * Three-tier pricing grid (Sprint · Project featured · Embedded). The featured
 * middle tier is ringed in brand and visually lifted; the others sit flat. One
 * column on mobile, three from `md`. Tokens only.
 *
 * Static content — no entrance motion of its own beyond inheriting the page; the
 * resting state renders server-side so SSR and reduced-JS visitors see all tiers.
 */
export function Pricing() {
	const { t } = useTranslation('public')
	return (
		<section
			id="pricing"
			aria-labelledby="pricing-heading"
			className="container scroll-mt-20 py-24"
		>
			<div className="mx-auto max-w-2xl text-center">
				<p className="text-brand text-body-xs tracking-eyebrow font-semibold uppercase">
					{t('landing.pricing.eyebrow')}
				</p>
				<h2
					id="pricing-heading"
					className="tracking-h2 mt-3 text-3xl font-semibold text-balance"
				>
					{t('landing.pricing.heading')}
				</h2>
				<p className="text-muted-foreground mt-4 text-pretty">
					{t('landing.pricing.lead')}
				</p>
			</div>

			<ul
				role="list"
				className="mx-auto mt-16 grid max-w-5xl items-start gap-6 md:grid-cols-3"
			>
				{TIERS.map((tier) => {
					const features = t(`landing.pricing.tiers.${tier.key}.features`, {
						returnObjects: true,
					}) as string[]
					return (
						<li key={tier.key}>
							<Card
								variant={tier.featured ? 'raised' : 'flat'}
								className={cn(
									'h-full gap-0 p-6',
									tier.featured &&
										'ring-brand relative ring-2 md:-translate-y-2',
								)}
							>
								{tier.featured ? (
									<span className="bg-brand text-primary-foreground text-body-2xs absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 font-semibold">
										{t('landing.pricing.featured')}
									</span>
								) : null}

								<h3 className="text-lg font-semibold">
									{t(`landing.pricing.tiers.${tier.key}.name`)}
								</h3>
								<p className="mt-2 flex items-baseline gap-1.5">
									<span className="tracking-h2 text-3xl font-semibold">
										{tier.price}
									</span>
									<span className="text-muted-foreground text-body-xs">
										{t(`landing.pricing.tiers.${tier.key}.cadence`)}
									</span>
								</p>
								<p className="text-muted-foreground text-body-xs mt-3 text-pretty">
									{t(`landing.pricing.tiers.${tier.key}.blurb`)}
								</p>

								<ul
									role="list"
									className="text-body-xs mt-6 flex flex-col gap-3"
								>
									{features.map((feature) => (
										<li key={feature} className="flex items-start gap-2.5">
											<Icon
												name="check"
												className="text-brand mt-0.5 shrink-0"
												aria-hidden
											/>
											<span>{feature}</span>
										</li>
									))}
								</ul>

								<Button
									asChild
									variant={tier.featured ? 'default' : 'outline'}
									className="mt-8 w-full"
								>
									<a href="#contact">
										{t(`landing.pricing.tiers.${tier.key}.cta`)}
									</a>
								</Button>
							</Card>
						</li>
					)
				})}
			</ul>
		</section>
	)
}
